import React from 'react';
import {
  makeStyles,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CardActionArea,
  Chip,
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import CodeIcon from '@material-ui/icons/Code';
import WebIcon from '@material-ui/icons/Web';
import StorageIcon from '@material-ui/icons/Storage';
import CloudIcon from '@material-ui/icons/Cloud';
import { Link } from '@backstage/core-components';
import { dark, light } from '../../theme/tokens';

const useStyles = makeStyles(theme => {
  const t = theme.palette.type === 'dark' ? dark : light;
  return {
    sectionHeader: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.75),
    },
    sectionTitle: {
      fontSize: '0.9375rem',
      fontWeight: 700,
      color: t.text,
      margin: 0,
    },
    viewAll: {
      fontSize: '0.8125rem',
      fontWeight: 600,
    },
    templateCard: {
      height: '100%',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        borderColor: t.primaryBorder,
        boxShadow: t.cardShadowLg,
      },
    },
    templateContent: {
      padding: theme.spacing(2.5),
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    templateHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 40,
      borderRadius: 10,
      background: t.surfaceSubtle,
      border: `1px solid ${t.border}`,
      color: t.primary,
      flexShrink: 0,
    },
    templateTitle: {
      fontWeight: 700,
      fontSize: '0.9375rem',
      color: t.text,
    },
    templateDescription: {
      color: t.textMuted,
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      flex: 1,
    },
    tagsContainer: {
      display: 'flex',
      gap: theme.spacing(0.75),
      flexWrap: 'wrap',
    },
    typeTag: {
      height: 22,
      fontSize: '0.6875rem',
      fontWeight: 600,
      backgroundColor: t.primary50,
      color: t.primary700,
    },
    tag: {
      height: 22,
      fontSize: '0.6875rem',
      fontWeight: 500,
      color: t.textMuted,
    },
    emptyState: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: t.textMuted,
    },
  };
});

const getIconForType = (type: string) => {
  switch (type.toLowerCase()) {
    case 'service':
    case 'microservice':
      return <StorageIcon />;
    case 'website':
    case 'frontend':
      return <WebIcon />;
    case 'library':
      return <CodeIcon />;
    default:
      return <CloudIcon />;
  }
};

export const FeaturedTemplates = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);

  const { value: templates, loading } = useAsync(async () => {
    try {
      const response = await catalogApi.getEntities({
        filter: { kind: 'Template' },
      });

      // Get first 6 templates
      return response.items.slice(0, 6).map(template => {
        const type = typeof template.spec?.type === 'string' ? template.spec.type : 'service';
        const tags = Array.isArray(template.metadata.tags) 
          ? template.metadata.tags.filter((tag): tag is string => typeof tag === 'string')
          : [];
        
        return {
          id: template.metadata.name,
          title: template.metadata.title || template.metadata.name,
          description: template.metadata.description || 'No description available',
          type,
          tags,
          namespace: template.metadata.namespace || 'default',
        };
      });
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  }, []);

  const handleTemplateClick = (template: any) => {
    navigate(`/create/templates/${template.namespace}/${template.id}`);
  };

  const header = (
    <Box className={classes.sectionHeader}>
      <Typography component="h2" className={classes.sectionTitle}>
        Featured templates
      </Typography>
      <Link to="/create" className={classes.viewAll}>
        View all templates
      </Link>
    </Box>
  );

  if (loading) {
    return (
      <Box>
        {header}
        <Typography className={classes.emptyState}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <Box>
        {header}
        <Typography className={classes.emptyState}>
          No templates available. Create your first template to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      <Grid container spacing={2}>
        {templates.map((template, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card className={classes.templateCard}>
              <CardActionArea onClick={() => handleTemplateClick(template)}>
                <CardContent className={classes.templateContent}>
                  <Box className={classes.templateHeader}>
                    <Box className={classes.iconWrapper}>
                      {getIconForType(template.type)}
                    </Box>
                    <Typography className={classes.templateTitle}>
                      {template.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={classes.templateDescription}>
                    {template.description}
                  </Typography>
                  <Box className={classes.tagsContainer}>
                    <Chip
                      label={template.type}
                      size="small"
                      className={classes.typeTag}
                    />
                    {template.tags.slice(0, 2).map((tag: string, tagIndex: number) => (
                      <Chip
                        key={tagIndex}
                        label={tag}
                        size="small"
                        variant="outlined"
                        className={classes.tag}
                      />
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

